import { create } from 'zustand';
import type { User } from '../auth/types';
import { db } from '../sync/db';
import * as api from './api';
import type { CreateOrderPayload, OrderStatus, VisitResultSummary, WorkOrder } from './types';

interface OrderState {
  orders: WorkOrder[];
  loading: boolean;
  error: string | null;
  requestEpoch: number;
  fetchAll: () => Promise<void>;
  fetchMine: (userId: string, companyId: string) => Promise<void>;
  fetchOne: (orderId: string, user?: User) => Promise<WorkOrder | null>;
  createOrder: (payload: CreateOrderPayload, user: User) => Promise<WorkOrder>;
  assignTechnician: (orderId: string, technicianId: string) => Promise<void>;
  setHelpers: (orderId: string, helperUserIds: string[]) => Promise<void>;
  changeStatus: (orderId: string, status: OrderStatus, extra?: Partial<WorkOrder>) => Promise<void>;
  setOrders: (orders: WorkOrder[]) => void;
  upsertOrder: (order: WorkOrder) => void;
  updateLocalStatus: (orderId: string, status: OrderStatus, patch?: Partial<WorkOrder>) => void;
  setResult: (orderId: string, resultSummary: VisitResultSummary) => void;
  reset: () => void;
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Ошибка загрузки';
}

function upsertOrderInList(orders: WorkOrder[], order: WorkOrder): WorkOrder[] {
  const existingIndex = orders.findIndex((item) => item.id === order.id);
  if (existingIndex === -1) {
    return [order, ...orders];
  }

  return orders.map((item) => (item.id === order.id ? order : item));
}

function patchOrderInList(
  orders: WorkOrder[],
  orderId: string,
  patch: Partial<WorkOrder>,
): WorkOrder[] {
  return orders.map((order) => (order.id === orderId ? { ...order, ...patch } : order));
}

export const useOrderStore = create<OrderState>()((set, get) => {
  const loadOrders = async (
    loadRemote: () => Promise<WorkOrder[]>,
    loadCached: () => Promise<WorkOrder[]>,
  ) => {
    const requestEpoch = get().requestEpoch;
    const isFirstLoad = get().orders.length === 0;
    set(isFirstLoad ? { loading: true, error: null } : { error: null });

    try {
      const orders = await loadRemote();
      if (get().requestEpoch !== requestEpoch) return;
      set({ orders, loading: false, error: null });
    } catch (error) {
      const cached = await loadCached();
      if (get().requestEpoch !== requestEpoch) return;
      set({ orders: cached, loading: false, error: getErrorMessage(error) });
    }
  };

  return {
    orders: [],
    loading: false,
    error: null,
    requestEpoch: 0,

    fetchAll: async () => {
      await loadOrders(
        async () => {
          const { useAuthStore } = await import('../auth/authStore');
          const user = useAuthStore.getState().user;
          if (!user) {
            return [];
          }
          return api.fetchOrdersForDispatcher(user);
        },
        () => db.orders.toArray(),
      );
    },

    fetchMine: async (userId, companyId) => {
      await loadOrders(
        () => api.fetchOrdersForTechnician(userId, companyId),
        async () => {
          const cached = await db.orders.where('assigned_to').equals(userId).toArray();
          return cached.filter((order) => order.company_id === companyId);
        },
      );
    },

    fetchOne: async (orderId, user) => {
      try {
        const order = await api.fetchOrderById(orderId, user);
        if (order) get().upsertOrder(order);
        return order;
      } catch {
        return (await db.orders.get(orderId)) ?? null;
      }
    },

    createOrder: async (payload, user) => {
      const order = await api.createOrder(payload, user);
      get().upsertOrder(order);
      return order;
    },

    assignTechnician: async (orderId, technicianId) => {
      await api.assignTechnician(orderId, technicianId);
      get().updateLocalStatus(orderId, 'assigned', { assigned_to: technicianId });
    },

    setHelpers: async (orderId, helperUserIds) => {
      await api.setOrderHelpers(orderId, helperUserIds);
      // Re-pull this order from Dexie so the in-memory list picks up
      // the freshly written helpers array. setOrderHelpers writes to
      // Dexie before returning so this is a cheap fast read.
      const fresh = await db.orders.get(orderId);
      if (fresh) {
        set((state) => ({ orders: upsertOrderInList(state.orders, fresh) }));
      }
    },

    changeStatus: async (orderId, status, extra = {}) => {
      // 1.16.0 — для audit log нужен предыдущий статус. Читаем до патча.
      const prev = get().orders.find((o) => o.id === orderId);
      const previousStatus = prev?.status ?? null;

      const patch: Partial<WorkOrder> = {
        status,
        updated_at: new Date().toISOString(),
        ...extra,
      };

      set((state) => ({ orders: patchOrderInList(state.orders, orderId, patch) }));

      const { useAuthStore } = await import('../auth/authStore');
      const user = useAuthStore.getState().user;
      await api.updateOrderStatus(orderId, status, extra, user ?? undefined);

      // 1.16.0 — записываем событие. Разделяем status_changed и
      // help_requested/cleared, потому что help — параллельная сигнализация,
      // не смена статуса. Если в `extra` пришёл help_requested — пишем
      // help-событие; если статус реально поменялся — пишем status_changed.
      const { writeAuditEvent } = await import('../audit_log/api');
      if (previousStatus !== null && previousStatus !== status) {
        void writeAuditEvent(user, {
          entity_type: 'work_order',
          entity_id: orderId,
          action: 'order_status_changed',
          payload: {
            from: previousStatus,
            to: status,
            ...(extra.completion_note ? { note: extra.completion_note } : {}),
            ...(extra.failure_reason ? { failure_reason: extra.failure_reason } : {}),
          },
        });
      }
      // help_requested менялся отдельно от status — отдельный event.
      if ('help_requested' in extra) {
        const helpRequested = (extra as { help_requested?: boolean }).help_requested;
        void writeAuditEvent(user, {
          entity_type: 'work_order',
          entity_id: orderId,
          action: helpRequested ? 'order_help_requested' : 'order_help_cleared',
          payload: helpRequested
            ? { comment: (extra as { help_comment?: string }).help_comment ?? null }
            : null,
        });
      }
    },

    setOrders: (orders) => set({ orders }),

    upsertOrder: (order) =>
      set((state) => ({
        orders: upsertOrderInList(state.orders, order),
      })),

    updateLocalStatus: (orderId, status, patch = {}) =>
      set((state) => ({
        orders: patchOrderInList(state.orders, orderId, { status, ...patch }),
      })),

    setResult: (orderId, resultSummary) =>
      set((state) => ({
        orders: patchOrderInList(state.orders, orderId, {
          result_summary: resultSummary,
          // 1.19.4 — Раньше тут хардкодилось status='done'. Это было
          // неправильно даже до 1.19.4 (для exceptional outcome'ов
          // result_summary тоже строится, но статус должен быть
          // awaiting_*/failed_*/...). После 1.19.4 финальный статус
          // выставляется только processFinalizeVisit через RPC. setResult
          // вызывается в buildResultSummary внутри outbox processing —
          // он обновляет агрегаты, не lifecycle.
        }),
      })),

    reset: () =>
      set((state) => ({
        orders: [],
        loading: false,
        error: null,
        requestEpoch: state.requestEpoch + 1,
      })),
  };
});
